import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@rtls-environment/environment';
import { LocaleService } from '@rtls/core/locale/locale.service';
import { ExternalIdentifiable } from '@rtls/core/models/base/external-identifiable.model';
import { Result } from '@rtls/core/models/result.model';
import { FileFormatHeader } from '@rtls/core/models/rpc/file-format-header';

import { Pagination } from '@rtls/core/models/rpc/pagination.model';
import { SearchQuery } from '@rtls/core/models/rpc/search-query';
import { RtlsModels } from '@rtls/core/models/rtls/rtls-models';
import isEmpty from 'lodash-es/isEmpty';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { modifyQueryAttributes } from '../../rpc/search-query.util';
import { Sorting } from '../../rpc/sorting.model';
import { RtlsExternalService } from '../rtls-external-service.model';
import { SupportedLocale } from '@rtls/core/locale/locale.enum';
import { v4 as uuidv4 } from 'uuid';

@Injectable()
export class BackendRtlsExternalService<T extends ExternalIdentifiable> implements RtlsExternalService<T> {
	private baseUrl: string;
	private searchEndPoint: string;
	private requestParameters: HttpParams;

	constructor(
		private _http: HttpClient,
		private localeService: LocaleService,
	) {}

	useModel(
		type: RtlsModels,
		baseUrl?: string,
		requestParameters?: HttpParams,
		searchEndpoint?: string,
	): RtlsExternalService<T> {
		const service = new BackendRtlsExternalService<T>(this._http, this.localeService);
		service.baseUrl = baseUrl ? `${baseUrl}/${type}` : `${environment.thingsApi}/${type}`;
		service.requestParameters = requestParameters === undefined ? new HttpParams() : requestParameters;
		service.searchEndPoint = !searchEndpoint ? '' : '' + searchEndpoint;
		return service;
	}

	get(uuid: string, subRoute?: string, query?: SearchQuery): Observable<T> {
		const params = this.requestParameters === undefined ? new HttpParams() : this.requestParameters;
		const requestParams = SearchQuery.toHttpParams(query, params);
		return this.wrap(
			this._http.get<T>(`${this.baseUrl}/${uuid}${subRoute ? subRoute : ''}`, {
				params: requestParams,
			}),
		);
	}

	getSubRoute(subRoute: string, httpParams?: HttpParams): Observable<any> {
		let currentParams = this.requestParameters === undefined ? new HttpParams() : this.requestParameters;
		if (httpParams != null) {
			httpParams
				.keys()
				.forEach((key) => (currentParams = currentParams.append(key, String(httpParams.get(key)))));
		}
		return this.wrap(
			this._http.get(`${this.baseUrl}/${subRoute}`, {
				params: currentParams,
			}),
		);
	}

	getByExternalId(extId: string, provider: string): Observable<T> {
		return this.wrap(
			this._http.get<T>(`${this.baseUrl}/${provider}/${extId}`, {
				params: this.requestParameters,
			}),
		);
	}

	getAll(pagination?: Pagination): Observable<Result<T>> {
		pagination = pagination ? pagination : Pagination.unlimited();
		return this.wrap(
			this._http.get<Result<T>>(`${this.baseUrl}/`, {
				params: Pagination.toHttpParams(pagination, this.requestParameters),
			}),
		);
	}

	getAllArray(): Observable<T[]> {
		return this.wrap(
			this._http.get<T[]>(`${this.baseUrl}/`, {
				params: Pagination.toHttpParams(Pagination.unlimited(), this.requestParameters),
			}),
		);
	}

	getTotalItems(): Observable<number | undefined> {
		return this.search(null, Pagination.default(1, 1)).pipe(map((data: Result<T>) => data.pagination.totalItems));
	}

	search(query: SearchQuery | null, pagination: Pagination, fuzzy: boolean = false): Observable<Result<T>> {
		const fuzzyParams = new HttpParams().set('fuzzy', String(false));
		return this.searchWithParams(query, pagination, fuzzyParams);
	}

	searchWithParams(
		query: SearchQuery | null,
		pagination: Pagination,
		httpParams?: HttpParams,
	): Observable<Result<T>> {
		let params = Pagination.toHttpParams(pagination, this.requestParameters);
		// Insert parent params
		if (httpParams != null) {
			httpParams.keys().forEach((key) => (params = params.append(key, String(httpParams.get(key)))));
		}
		const requestParams = SearchQuery.toHttpParams(query, params);
		return this.wrap(
			this._http.get<Result<T>>(`${this.baseUrl}/${this.searchEndPoint}`, {
				params: Pagination.toHttpParams(pagination, requestParams),
			}),
		);
	}

	searchWithParams2(
		query: SearchQuery | null,
		pagination: Pagination,
		httpParams?: HttpParams,
		httpHeader?: HttpHeaders,
	): Observable<Result<T>> {
		let params = this.convert(pagination);
		// Insert parent params
		if (httpParams != null) {
			httpParams.keys().forEach((key) => (params = params.append(key, String(httpParams.get(key)))));
		}
		const preprocessedQuery = this.preProcess(query);
		return this.wrap(
			this._http.post<Result<T>>(`${this.baseUrl}/${this.searchEndPoint}`, preprocessedQuery, {
				params: params,
				headers: httpHeader,
			}),
		);
	}

	preProcess(query: SearchQuery | null): SearchQuery | null {
		if (!!query) {
			const suffix = this.localeService.getLanguageCode() === SupportedLocale.FR ? '' : '_en';
			return modifyQueryAttributes(
				query,
				(attr) => attr.endsWith('translation'),
				(attr) => attr + suffix,
			);
		}
		return query;
	}

	// Some search query use the `translation` field => when searching in english it should point to `translation_en`

	protected convert(pagination: Pagination = Pagination.default()): HttpParams {
		const sorting =
			pagination.sorting == null
				? ''
				: pagination.sorting
						.map((sortEntry: Sorting) => `${sortEntry.direction}${sortEntry.attribute}`)
						.join(',');
		return new HttpParams()
			.set('pageNumber', String(pagination.pageNumber))
			.set('pageSize', String(pagination.pageSize))
			.set('sorting', sorting);
	}

	upsert(original: T, updated: T): Observable<T> {
		if (isEmpty(original) || !original.uuid) {
			updated.extId = updated.extId || uuidv4();
			updated.provider = '_INTERNAL_';
			return this.save(updated);
		}
		return this.saveByPut(updated, original.uuid);
	}

	save(body: T): Observable<T> {
		return this.wrap(
			this._http.post<T>(`${this.baseUrl}/`, body, {
				params: this.requestParameters,
			}),
		);
	}

	saveByPut(body: T, uuid?: string): Observable<T> {
		return this.wrap(
			this._http.put<T>(uuid ? `${this.baseUrl}/${uuid}` : `${this.baseUrl}`, body, {
				params: this.requestParameters,
			}),
		);
	}

	delete(uuid: string): Observable<T> {
		return this.wrap(
			this._http.delete<T>(`${this.baseUrl}/${uuid}`, {
				params: this.requestParameters,
			}),
		);
	}

	deleteAll(): Observable<boolean> {
		return this.wrap(this._http.delete<boolean>(`${this.baseUrl}/search?pageSize=-1`));
	}

	bulkDelete(endpoint: string, uuids: string[]): Observable<T[]> {
		return this.wrap(
			this._http.delete<T[]>(`${this.baseUrl}/${endpoint}`, {
				params: this.requestParameters.append('uuids', uuids.join(',')),
			}),
		);
	}

	downloadResource(
		query: SearchQuery | null,
		pagination: Pagination,
		service: string,
		format: 'xlsx' | 'pdf',
	): Observable<Blob> {
		const params = Pagination.toHttpParams(pagination, this.requestParameters);
		const headers = new HttpHeaders({
			Accept: FileFormatHeader.getCorrespondingAcceptHeaderValue(format),
		});

		const requestParams = SearchQuery.toHttpParams(query, params);
		return this.wrap<Blob>(
			this._http.get(`${this.baseUrl}/${service}`, {
				params: Pagination.toHttpParams(pagination, requestParams),
				headers: headers,
				responseType: 'blob',
			}),
		);
	}

	// add general behaviour
	wrap<X>(observable: Observable<X>): Observable<X> {
		return observable;
	}

	/**
	 * Triggers an action on the given resource
	 * @param command - the action endpoint
	 * @param uuid - the resource uuid
	 * @param payload - the payload to send along
	 * @example exec('/do-whatever', 'a-uuid', 'payload')
	 */
	exec(command: string, uuid = '', payload = {}): Observable<T> {
		const url = !!uuid ? `${this.baseUrl}/${uuid}${command}?version=v1` : `${this.baseUrl}/${command}?version=v1`;
		return this.wrap(this._http.post<T>(url, payload || {}));
	}

	getFitlersValues(): Observable<any> {
		return this.wrap(this._http.get<T>(`${this.baseUrl}/filters?version=v1`, {}));
	}
}
